import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  computed,
  effect,
  input,
  output,
  signal,
  untracked,
  viewChild,
} from '@angular/core';
import { NgIcon, provideIcons } from '@ng-icons/core';
import {
  lucideFileUp,
  lucideLayoutTemplate,
  lucideRectangleHorizontal,
  lucideRectangleVertical,
  lucideRotateCcw,
  lucideRuler,
  lucideX,
} from '@ng-icons/lucide';
import { HlmButtonImports } from '@spartan-ng/helm/button';
import { HlmCardImports } from '@spartan-ng/helm/card';
import { HlmInputImports } from '@spartan-ng/helm/input';
import { HlmLabelImports } from '@spartan-ng/helm/label';
import { HlmSpinnerImports } from '@spartan-ng/helm/spinner';
import { HlmTypographyImports } from '@spartan-ng/helm/typography';
import { VoucherDesign, artworkLabel, ptToCm } from '../../../../services/voucher-design.service';
import { takeSelectedFile } from '../../../../utils/image-file.utils';
import { validateDesignFile } from '../../../../utils/design-file.utils';
import { ColorFieldComponent } from '../color-field/color-field.component';

const MM_PER_PT = 25.4 / 72;

/** Paper formats in mm, portrait. Applied in the artwork's own orientation. */
const PRESETS: { label: string; width: number; height: number }[] = [
  { label: 'A6', width: 105, height: 148 },
  { label: 'A5', width: 148, height: 210 },
  { label: 'A4', width: 210, height: 297 },
  { label: 'A3', width: 297, height: 420 },
  { label: 'DIN lang', width: 99, height: 210 },
];

/**
 * The page itself, shown when no element is selected: how big the voucher prints,
 * and controls to print it bigger or smaller (percent, width or height in mm,
 * paper formats). The elements scale along on the server.
 */
@Component({
  selector: 'app-page-panel',
  standalone: true,
  imports: [
    ColorFieldComponent,
    NgIcon,
    HlmButtonImports,
    HlmCardImports,
    HlmInputImports,
    HlmLabelImports,
    HlmSpinnerImports,
    HlmTypographyImports,
  ],
  providers: [
    provideIcons({
      lucideFileUp,
      lucideLayoutTemplate,
      lucideRectangleHorizontal,
      lucideRectangleVertical,
      lucideRotateCcw,
      lucideRuler,
      lucideX,
    }),
  ],
  templateUrl: './page-panel.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PagePanelComponent {
  readonly design = input.required<VoucherDesign>();
  readonly dirty = input(false);
  readonly busy = input(false);
  readonly applyScale = output<number>();
  /** Background changes: a file, one of our artworks (opens the picker), none, a colour. */
  readonly backgroundFile = output<File>();
  readonly pickTemplate = output<void>();
  readonly removeBackground = output<void>();
  /** Turn a page without artwork (true: landscape). */
  readonly orientation = output<boolean>();
  readonly pageColor = output<string>();
  readonly fileError = output<string>();

  private readonly fileInput = viewChild<ElementRef<HTMLInputElement>>('fileInput');

  readonly backgroundLabel = computed(() => {
    const d = this.design();
    switch (d.background_kind) {
      case 'file':
        return d.source_filename ?? 'Hochgeladene Datei';
      case 'template':
        return `Vorlage „${artworkLabel(d.background_template ?? '')}“`;
      default:
        return 'Kein Hintergrund, eine Seite in einer Farbe';
    }
  });
  readonly hasUpload = computed(() => this.design().background_kind === 'file');
  /** Only a page without artwork can be turned; a file or template brings its own. */
  readonly canTurn = computed(() => this.design().background_kind === 'none');

  /** The scale being edited, applied only on "Übernehmen". */
  readonly draft = signal(1);

  readonly sourceWidthMm = computed(() => this.design().source_width_pt * MM_PER_PT);
  readonly sourceHeightMm = computed(() => this.design().source_height_pt * MM_PER_PT);
  readonly draftWidthMm = computed(() => Math.round(this.sourceWidthMm() * this.draft()));
  readonly draftHeightMm = computed(() => Math.round(this.sourceHeightMm() * this.draft()));
  readonly draftPercent = computed(() => Math.round(this.draft() * 100));
  readonly printedLabel = computed(
    () => `${ptToCm(this.design().page_width_pt)} × ${ptToCm(this.design().page_height_pt)} cm`,
  );
  readonly sourceLabel = computed(
    () => `${ptToCm(this.design().source_width_pt)} × ${ptToCm(this.design().source_height_pt)} cm`,
  );
  readonly rangeLabel = computed(
    () =>
      `${Math.ceil(this.design().min_print_scale * 100)} – ${Math.floor(this.design().max_print_scale * 100)} %`,
  );
  readonly inRange = computed(() => {
    const d = this.design();
    return this.draft() >= d.min_print_scale - 1e-6 && this.draft() <= d.max_print_scale + 1e-6;
  });
  readonly changed = computed(() => Math.abs(this.draft() - this.design().print_scale) > 1e-4);
  readonly landscape = computed(
    () => this.design().source_width_pt > this.design().source_height_pt,
  );
  readonly presets = computed(() =>
    PRESETS.map((p) => {
      const [w, h] = this.landscape() ? [p.height, p.width] : [p.width, p.height];
      // fit inside the format, keeping the artwork's proportions
      const scale = Math.min(w / this.sourceWidthMm(), h / this.sourceHeightMm());
      const d = this.design();
      return {
        label: p.label,
        hint: `${w} × ${h} mm`,
        scale,
        possible: scale >= d.min_print_scale - 1e-6 && scale <= d.max_print_scale + 1e-6,
      };
    }),
  );

  constructor() {
    // A new or rescaled design resets the draft to what is saved.
    effect(() => {
      const scale = this.design().print_scale;
      untracked(() => this.draft.set(scale));
    });
  }

  onPercent(event: Event) {
    const value = parseFloat((event.target as HTMLInputElement).value);
    if (Number.isFinite(value) && value > 0) this.draft.set(value / 100);
  }

  onWidthMm(event: Event) {
    const value = parseFloat((event.target as HTMLInputElement).value);
    if (Number.isFinite(value) && value > 0) this.draft.set(value / this.sourceWidthMm());
  }

  onHeightMm(event: Event) {
    const value = parseFloat((event.target as HTMLInputElement).value);
    if (Number.isFinite(value) && value > 0) this.draft.set(value / this.sourceHeightMm());
  }

  apply() {
    if (this.inRange() && this.changed())
      this.applyScale.emit(Math.round(this.draft() * 10000) / 10000);
  }

  openFilePicker() {
    this.fileInput()?.nativeElement.click();
  }

  onFileSelected(event: Event) {
    const file = takeSelectedFile(event);
    if (!file) return;
    const problem = validateDesignFile(file);
    if (problem) this.fileError.emit(problem);
    else this.backgroundFile.emit(file);
  }

  turn(landscape: boolean) {
    if (this.canTurn() && landscape !== this.landscape()) this.orientation.emit(landscape);
  }

  onPageColor(value: string) {
    if (value !== this.design().background_color) this.pageColor.emit(value);
  }
}
